import React, { useEffect, useMemo, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { FieldRenderer } from '../components/FieldRenderer';
import { RaciAssignments } from '../components/RaciAssignments';
import { LinkedArtefacts } from '../components/LinkedArtefacts';
import { DocumentManager } from '../components/DocumentManager';
import { RequirementsTracker } from '../components/RequirementsTracker';
import { RaiseArtefactModal } from '../components/RaiseArtefactModal';
import Select from 'react-select';
import { reactSelectStyles } from '../utils/reactSelectStyles';
import { controlApi, controlMatrixApi, peopleApi, departmentsApi, raciApi, evidenceApi, evidenceSubmissionsApi } from '../services/api';
import type { FieldConfig, FieldValues, Person, Department } from '../types/models';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { getMissingRequiredFields } from '../utils/fieldValidation';
import { maturityToScore, isImplementationComplete } from '../utils/controlConformity';
import { recalculateRisksLinkedToControl } from '../utils/riskScoring';

const getDefaultFields = (): FieldConfig[] => [
  {
    fieldId: 'control_id',
    fieldName: 'Control ID',
    // Was 'readonly'/isEditable: false - FieldRenderer's 'readonly' type is always
    // non-editable regardless of isEditable (see FieldRenderer.tsx), so a control whose
    // matrix has no saved field config (hitting this fallback, not ControlConfigPage's
    // own default template) could never have its reference code set or corrected.
    // ControlConfigPage.tsx's own default template already treats this as a normal
    // editable text field - this fallback just hadn't matched that. isRequired stays
    // false: a prior fix (required + uneditable = creation permanently blocked) already
    // addressed that deadlock by dropping the requirement, not by making it editable.
    fieldType: 'text',
    cardNumber: 1,
    isRequired: false,
    isEditable: true,
    isVisible: true,
  },
  {
    fieldId: 'control_title',
    fieldName: 'Control Title',
    fieldType: 'text',
    cardNumber: 1,
    isRequired: true,
    isEditable: true,
    isVisible: true,
  },
  {
    fieldId: 'control_definition',
    fieldName: 'Control Definition',
    fieldType: 'richtext',
    cardNumber: 1,
    isRequired: false,
    isEditable: true,
    isVisible: true,
  },
];

// Maturity label/score parsing (maturityToScore) and the implementation-status check
// (isImplementationComplete) now live in ../utils/controlConformity - shared with
// StatementOfApplicabilityPage.tsx so the same control reads the same way everywhere.

const JUSTIFICATION_OPTIONS = [
  { value: 'Business', label: 'Business' },
  { value: 'Regulatory', label: 'Regulatory' },
  { value: 'Contractual', label: 'Contractual' },
  { value: 'Risk', label: 'Risk' },
];

// Fields that never reach FieldRenderer on this page, so there is nowhere to
// attach an inline validation error even if they were required:
//   - justification_for_inclusion, control_effectiveness, control_requirement,
//     control_metrics, control_frequency, control_owner, business_function,
//     status: filtered out of the generic card loop below (shown in a
//     dedicated card elsewhere, or not rendered on this page at all).
//   - assigned_scope, justification_for_exclusion: rendered in the
//     Applicability card below via bespoke hand-rolled <select>/<ReactQuill>/
//     <Select> markup, not FieldRenderer.
const FIELDS_EXCLUDED_FROM_FIELD_RENDERER = [
  'justification_for_inclusion', 'control_effectiveness', 'control_requirement',
  'control_metrics', 'control_frequency', 'control_owner', 'business_function', 'status',
  'assigned_scope', 'justification_for_exclusion',
];

const ACCEPTANCE_REVIEW_PERIODS = ['Weekly', 'Monthly', 'Quarterly', 'Bi-Annual', 'Annual', 'Ad-hoc'] as const;
const calcAcceptanceUntil = (period: string, from: string): string => {
  if (!period || period === 'Ad-hoc' || !from) return '';
  const d = new Date(from);
  if (isNaN(d.getTime())) return '';
  switch (period) {
    case 'Weekly':    d.setDate(d.getDate() + 7); break;
    case 'Monthly':   d.setMonth(d.getMonth() + 1); break;
    case 'Quarterly': d.setMonth(d.getMonth() + 3); break;
    case 'Bi-Annual': d.setMonth(d.getMonth() + 6); break;
    case 'Annual':    d.setFullYear(d.getFullYear() + 1); break;
  }
  return d.toISOString().split('T')[0];
};

export const ControlDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const matrixId = searchParams.get('matrixId') || '';

  const { getTenantId } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  const [invalidFieldIds, setInvalidFieldIds] = useState<Set<string>>(new Set());
  // New control, or the list's Edit action (?edit=true), both start in edit mode - see RiskDetail.tsx.
  const [isEditing, setIsEditing] = useState(!id || searchParams.get('edit') === 'true');
  const [activeTab, setActiveTab] = useState<'details' | 'requirements' | 'attachments' | 'raci' | 'artefacts'>('details');
  const [editingAcceptance, setEditingAcceptance] = useState(false);
  const [acceptanceDraft, setAcceptanceDraft] = useState({ reason: '', owner: '', period: '', until: '', on: '' });
  const [showRaiseModal, setShowRaiseModal] = useState(false);

  // effectiveMatrixId starts from the URL param but falls back to the value stored
  // on the control record itself (populated once the control query resolves).
  // This ensures the full field config is shown when navigating here without ?matrixId=
  const [effectiveMatrixId, setEffectiveMatrixId] = useState(matrixId);

  const { data: matrix } = useQuery({
    queryKey: ['controlMatrix', effectiveMatrixId],
    queryFn: () => controlMatrixApi.getOne(effectiveMatrixId),
    enabled: !!effectiveMatrixId,
  });

  const fieldConfigs: FieldConfig[] = useMemo(() => {
    const raw = matrix?.matrix?.fields || matrix?.fields;
    if (Array.isArray(raw) && raw.length > 0) {
      // Normalize fields saved by ControlConfigPage (uses `visible`/`required`/`order`)
      // to the FieldConfig interface (uses `isVisible`/`isRequired`/`orderIndex`)
      return raw.map((f: any): FieldConfig => ({
        ...f,
        isVisible:   f.isVisible   !== undefined ? f.isVisible   : f.visible !== false,
        isEditable:  f.isEditable  !== undefined ? f.isEditable  : true,
        isRequired:  f.isRequired  !== undefined ? f.isRequired  : Boolean(f.required),
        orderIndex:  f.orderIndex  !== undefined ? f.orderIndex  : (f.order ?? 0),
        cardNumber:  f.cardNumber  !== undefined ? f.cardNumber  : 1,
      }));
    }
    return getDefaultFields();
  }, [matrix]);

  const cardDefinitions: Record<string, string> = useMemo(() => {
    const cards = matrix?.matrix?.cards || matrix?.cards;
    if (cards && typeof cards === 'object') return cards;
    return {
      '1': 'Control Information',
      '2': 'Classification & Ownership',
    };
  }, [matrix]);

  const { data: control } = useQuery({
    queryKey: ['control', id],
    queryFn: () => controlApi.getOne(id!),
    enabled: !!id,
    select: (data) => {
      if (!data) return data;
      
      // Deep sanitization helper
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        
        // If it's a {value, label} object, return just the value
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        
        // Otherwise recursively sanitize all properties
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      
      return sanitizeObject(data);
    },
  });

  // When there's no matrixId in the URL, use the one stored on the control record
  useEffect(() => {
    if (!matrixId && (control as any)?.matrixId) {
      setEffectiveMatrixId((control as any).matrixId);
    }
  }, [(control as any)?.matrixId, matrixId]);

  const { data: departments = [] } = useQuery<Department[]>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
  });

  const { data: people = [] } = useQuery<Person[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
    select: (data) => {
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        
        // If it's a {value, label} object, return just the value
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        
        // Otherwise recursively sanitize all properties
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      
      return sanitizeObject(data);
    },
  });

  useEffect(() => {
    const fields = control?.fields;
    if (fields && typeof fields === 'object') {
      // Sanitize field values to extract strings from {value, label} objects
      const sanitizedFields: FieldValues = {};
      Object.keys(fields).forEach((key) => {
        const val = fields[key];
        if (val && typeof val === 'object' && 'value' in val) {
          sanitizedFields[key] = val.value;
        } else {
          sanitizedFields[key] = val;
        }
      });
      setFieldValues(sanitizedFields);
    }
  }, [control]);

  // The single source of truth for what FieldRenderer actually renders on this
  // page: fieldConfigs filtered down to visible, FieldRenderer-reachable fields
  // (see FIELDS_EXCLUDED_FROM_FIELD_RENDERER above), with the exact same
  // per-field isEditable/fieldType overrides that used to be computed inline
  // inside the JSX .map() at the FieldRenderer call site. groupedFields (for
  // rendering) AND getMissingRequiredFields (for required-field validation)
  // both read from this same memo, so they cannot drift out of sync.
  const effectiveFieldConfigs: FieldConfig[] = useMemo(() => {
    return fieldConfigs
      .filter((config) => config.isVisible)
      .filter((config) => !FIELDS_EXCLUDED_FROM_FIELD_RENDERER.includes(config.fieldId))
      .map((config) => ({
        ...config,
        isEditable: isEditing
          ? (config.fieldId === 'next_test_date' ? true
            : config.fieldId === 'gap_to_target' ? false
            : config.isEditable)
          : false,
        // next_test_date: always a date input. implementation_date: same class of bug -
        // whatever fieldType got frozen into this matrix's saved field config (or the
        // catalog row it was copied from) isn't 'date', so it renders as a plain text box.
        // Only the type is forced here, not isEditable - the reported bug was the field
        // type, not that the field couldn't be edited.
        fieldType: config.fieldId === 'next_test_date' || config.fieldId === 'implementation_date'
          ? 'date' : config.fieldType,
      }));
  }, [fieldConfigs, isEditing]);

  const groupedFields = useMemo(() => {
    return effectiveFieldConfigs.reduce((acc: Record<number, FieldConfig[]>, config) => {
      const cardNum = Number(config.cardNumber || 1);
      if (!acc[cardNum]) acc[cardNum] = [];
      acc[cardNum].push(config);
      return acc;
    }, {});
  }, [effectiveFieldConfigs]);

  // Normalise justification_for_inclusion to a string[] regardless of whether the
  // stored value is the old single-string format or the current array format.
  const justificationForInclusionValues = useMemo<string[]>(() => {
    const raw = fieldValues['justification_for_inclusion'];
    if (Array.isArray(raw)) return raw as string[];
    if (typeof raw === 'string' && raw) return [raw];
    return [];
  }, [fieldValues]);

  // Auto-calculate next_test_date from last_test_date + test_frequency
  useEffect(() => {
    const lastDate = String(fieldValues['last_test_date'] || '').trim();
    const freq = String(fieldValues['test_frequency'] || '').trim().toLowerCase();
    if (!lastDate || !freq || freq === 'ad-hoc') return;

    const base = new Date(lastDate);
    if (isNaN(base.getTime())) return;

    const next = new Date(base);
    if (freq === 'continuous' || freq === 'daily')       next.setDate(next.getDate() + 1);
    else if (freq === 'weekly')                           next.setDate(next.getDate() + 7);
    else if (freq === 'monthly')                         next.setMonth(next.getMonth() + 1);
    else if (freq === 'quarterly')                       next.setMonth(next.getMonth() + 3);
    else if (freq === 'semi-annual')                     next.setMonth(next.getMonth() + 6);
    else if (freq === 'annual')                          next.setFullYear(next.getFullYear() + 1);
    else return; // unknown frequency, don't overwrite

    const iso = next.toISOString().split('T')[0]; // YYYY-MM-DD
    setFieldValues((prev) => ({ ...prev, next_test_date: iso }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues['last_test_date'], fieldValues['test_frequency']]);

  // Fetch RACI assignments for this control
  const { data: raciAssignments = [] } = useQuery({
    queryKey: ['raci', 'control', id],
    queryFn: () => raciApi.getByArtefact('control', id!),
    enabled: !!id,
  });

  // Derive the evidence IDs linked to this control (only evidence-type artefacts)
  const linkedEvidenceIds = useMemo<string[]>(() => {
    const artefacts = Array.isArray((control as any)?.linked_artefacts) ? (control as any).linked_artefacts : [];
    return artefacts
      .filter((la: any) => {
        const t = String(la.artefact_type || la.type || '').toLowerCase();
        return !t || t === 'evidence';
      })
      .map((la: any) => String(la.artefact_id || la.targetId || la.sourceId || la.artefactId || la.evidenceId || la.id || ''))
      .filter(Boolean);
  }, [(control as any)?.linked_artefacts]);

  // Fetch only the specific evidence items linked to this control (not the whole register)
  const { data: linkedEvidenceObjs = [], isLoading: evidenceLoading } = useQuery<any[]>({
    queryKey: ['evidence', 'linked', id, linkedEvidenceIds.join(',')],
    queryFn: () =>
      Promise.all(
        linkedEvidenceIds.map((eid) =>
          evidenceApi
            .getOne(eid)
            .then((res: any) => res?.evidence ?? res) // unwrap { evidence: item } wrapper
            .catch(() => null),
        ),
      ).then((res) => res.filter(Boolean)),
    enabled: linkedEvidenceIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Multi-team rollup (RUB-50353 evidence overhaul, Part E): each linked evidence item may
  // itself have several team submissions underneath it. Fetched here (not just on the evidence
  // page) so the effectiveness calculation below can look one level deeper than "is this
  // evidence item reviewed" into "what fraction of its team submissions are Accepted" -
  // an item with zero submissions is entirely unaffected by this fan-out (falls back to its
  // own review-date fields exactly as before).
  const submissionQueries = useQueries({
    queries: linkedEvidenceObjs.map((ev: any) => ({
      // A DIFFERENT key from EvidenceRecordDetail.tsx's own ['evidenceSubmissions', id] query
      // on purpose - that page caches the raw { submissions: [...] } response object, this one
      // caches the unwrapped array. Same key, different shape: opening an evidence record and
      // then a control linked to it (or vice versa) would serve the other page's cached shape
      // from the query cache without re-running this queryFn, and subs.filter(...) below would
      // throw (subs.filter is not a function) the moment that happened to be the object.
      queryKey: ['controlEvidenceSubmissions', ev.evidenceId],
      queryFn: () => evidenceSubmissionsApi.list(ev.evidenceId).then((res: any) => res?.submissions || []),
      enabled: !!ev.evidenceId,
      staleTime: 5 * 60 * 1000,
    })),
  });
  const submissionsByEvidenceId = useMemo(() => {
    const map = new Map<string, any[]>();
    linkedEvidenceObjs.forEach((ev: any, i: number) => {
      map.set(ev.evidenceId, submissionQueries[i]?.data || []);
    });
    return map;
  }, [linkedEvidenceObjs, submissionQueries]);

  // Auto-compute Control Effectiveness — 3-factor model:
  //   Maturity 50% | Evidence presence & review 30% | Test recency 20%
  //   (Requirements tracker is available as a power-user feature but does not affect scoring)
  //
  // A factor that hasn't been assessed yet (maturity left at the "Unassessed"
  // placeholder, or no test ever logged AND no evidence linked) is EXCLUDED from
  // the average and its weight is redistributed across whatever factors ARE known,
  // rather than counted as a literal zero. Treating "not yet assessed" the same as
  // "assessed as ineffective" was silently capping every control at a low score the
  // moment even one factor was blank, which in turn made residual risk collapse to
  // inherent risk platform-wide regardless of how strong the control actually was.
  // Evidence presence is the one factor that's always "known" (zero linked evidence
  // is a real, observed state, not a missing assessment), so it never gets excluded.
  //
  // Evidence being LINKED used to be worth full credit on its own - a control could
  // score 90%+ with 3+ evidence items that had never actually been reviewed, which is
  // exactly the incongruence between this score and the ISO Conformity banner's own
  // "Evidence not yet reviewed" reason a few hundred lines down. Evidence is only
  // proof a control works once someone has actually looked at it, so the factor is
  // now presence-score * reviewedFraction (linked evidence with a real
  // evidence_last_reviewed/last_reviewed/last_review_date, divided by total linked) -
  // 3 items linked but 0 reviewed now scores 0, matching the banner's own reasoning
  // rather than silently disagreeing with it.
  //
  // Test recency is now DERIVED from the most recently reviewed linked evidence when
  // evidence exists, rather than the Control's own manually-typed last_test_date -
  // evidence is the proof a control was actually tested (confirmed live: only 1 of
  // 354 controls has ever had last_test_date populated at all, while evidence review
  // dates are populated far more often), so this is what "tied to evidence
  // submission" means in practice. The manual last_test_date/test_frequency fields
  // remain editable and are used verbatim only when no evidence is linked to derive
  // from - deliberately not building a separate "how was this tested" field for a
  // hypothetical non-evidence-backed testing path with no product intent behind it
  // today (confirmed: zero references anywhere in the live catalog).
  const effectivenessBreakdown = useMemo(() => {
    if (evidenceLoading) return null; // avoid a flash of "0 evidence" before the real count loads

    const factors: Array<{ key: 'maturity' | 'evidence' | 'testRecency'; label: string; score: number; weight: number }> = [];

    // 1. Maturity (50%) — "Unassessed"/blank means nobody has rated it yet.
    const maturityRaw = String(fieldValues['current_maturity'] || '').trim().toLowerCase();
    if (maturityRaw && maturityRaw !== 'unassessed') {
      const maturityNum = maturityToScore(fieldValues['current_maturity']);
      if (maturityNum !== null) {
        factors.push({ key: 'maturity', label: 'Maturity', score: (maturityNum / 5) * 100, weight: 0.50 });
      }
    }

    // 2. Evidence presence & review (30%) — presence graduated by count, then scaled by
    // how much of that linked evidence has actually been reviewed at least once.
    const linkedEvidenceCount = linkedEvidenceObjs.length;
    const EVIDENCE_COUNT_SCORE: Record<number, number> = { 0: 0, 1: 60, 2: 85 };
    const presenceScore = EVIDENCE_COUNT_SCORE[linkedEvidenceCount] ?? 100; // 3+ linked = full credit
    const isReviewed = (ev: any) => !!(
      ev.fields?.evidence_last_reviewed || ev.fields?.last_reviewed || ev.fields?.last_review_date
    );
    // Multi-team rollup: an item with zero submissions behaves exactly as before (binary
    // reviewed/not-reviewed via its own fields). An item WITH submissions contributes a
    // FRACTION instead - acceptedSubmissions/totalSubmissions - matching this score's existing
    // graduated-credit philosophy (the same spirit as the 0/60/85/100 count-based presence
    // score above): a requirement 2 of 3 teams have fully satisfied is genuinely more "done"
    // than zero, and less than all three.
    const reviewedWeight = linkedEvidenceObjs.reduce((sum: number, ev: any) => {
      const subs = submissionsByEvidenceId.get(ev.evidenceId) || [];
      if (subs.length === 0) return sum + (isReviewed(ev) ? 1 : 0);
      const accepted = subs.filter((s: any) => s.status === 'Accepted').length;
      return sum + accepted / subs.length;
    }, 0);
    const reviewedFraction = linkedEvidenceCount > 0 ? reviewedWeight / linkedEvidenceCount : 0;
    factors.push({ key: 'evidence', label: 'Evidence presence & review', score: presenceScore * reviewedFraction, weight: 0.30 });

    // 3. Test recency (20%) — derived from the most recently reviewed linked evidence
    // when there is any; falls back to the manually-typed last_test_date only for
    // controls with zero linked evidence. No date at all (no evidence review, no
    // manual entry) means never tested/unknown, so it's excluded rather than scored
    // as zero.
    // Most-recent-wins across both sources: each linked item's own review-date fields, AND
    // (multi-team rollup) every Accepted submission's reviewedAt across every linked item -
    // a team's own review is just as real a "this was tested" signal as the evidence item's
    // own fields, so it has to compete on equal footing, not be ignored in favour of a stale
    // top-level date.
    const evidenceFieldDates = linkedEvidenceObjs
      .map((ev: any) => String(ev.fields?.evidence_last_reviewed || ev.fields?.last_reviewed || ev.fields?.last_review_date || ''))
      .filter(Boolean);
    const acceptedSubmissionDates = linkedEvidenceObjs.flatMap((ev: any) =>
      (submissionsByEvidenceId.get(ev.evidenceId) || [])
        .filter((s: any) => s.status === 'Accepted' && s.reviewedAt)
        .map((s: any) => String(s.reviewedAt)),
    );
    const derivedFromEvidence = [...evidenceFieldDates, ...acceptedSubmissionDates]
      .map((d: string) => new Date(d))
      .filter((d: Date) => !isNaN(d.getTime()))
      .sort((a: Date, b: Date) => b.getTime() - a.getTime())[0];
    const lastDate = derivedFromEvidence
      ? derivedFromEvidence.toISOString().split('T')[0]
      : String(fieldValues['last_test_date'] || '').trim();
    const freq = String(fieldValues['test_frequency'] || '').trim().toLowerCase();
    if (lastDate) {
      const freqDays: Record<string, number> = {
        continuous: 1, daily: 1, weekly: 7, monthly: 30,
        quarterly: 90, 'semi-annual': 180, annual: 365,
      };
      const windowDays = freqDays[freq];
      let testScore: number;
      if (!windowDays || freq === 'ad-hoc') {
        testScore = 100; // ad-hoc / unknown frequency: any past test is fine
      } else {
        const daysSince = (Date.now() - new Date(lastDate).getTime()) / (1000 * 60 * 60 * 24);
        if (daysSince <= windowDays)          testScore = 100; // on schedule
        else if (daysSince <= windowDays * 2) testScore = 50;  // up to 1 window overdue
        else                                  testScore = 0;   // significantly overdue
      }
      factors.push({ key: 'testRecency', label: 'Test recency', score: testScore, weight: 0.20 });
    }

    const totalWeight = factors.reduce((sum, f) => sum + f.weight, 0);
    if (totalWeight === 0) return null;
    let weightedScore = factors.reduce((sum, f) => sum + f.score * f.weight, 0) / totalWeight;

    // An unimplemented control cannot read as "Effective" regardless of how strong its
    // other factors are - matches the ISO Conformity banner's own "Control is not fully
    // implemented" reason, which this score used to have no way of ever agreeing with.
    const implStatusRaw = String(fieldValues['implementation_status'] || '');
    const notApplicable = implStatusRaw.toLowerCase().includes('not applicable');
    const cappedByImplementation = !notApplicable && !isImplementationComplete(implStatusRaw);
    if (cappedByImplementation) weightedScore = Math.min(weightedScore, 40);

    return {
      score: Math.round(weightedScore),
      factorsAssessed: factors.length,
      totalFactors: 3,
      factors,
      testRecencySource: derivedFromEvidence ? 'evidence' as const : 'manual' as const,
      cappedByImplementation,
    };
  }, [
    fieldValues['current_maturity'],       // eslint-disable-line react-hooks/exhaustive-deps
    fieldValues['last_test_date'],         // eslint-disable-line react-hooks/exhaustive-deps
    fieldValues['test_frequency'],         // eslint-disable-line react-hooks/exhaustive-deps
    fieldValues['implementation_status'],  // eslint-disable-line react-hooks/exhaustive-deps
    linkedEvidenceObjs,
    submissionsByEvidenceId,
    evidenceLoading,
  ]);

  const computedEffectiveness = effectivenessBreakdown?.score ?? null;

  // Sync computed effectiveness into fieldValues so it gets saved. The
  // "(2/3 factors assessed)" suffix is informational only — the residual-risk
  // parser on the risk side just reads the leading number, so this stays
  // backward-compatible with anything already parsing "NN%".
  useEffect(() => {
    if (effectivenessBreakdown) {
      const confidenceSuffix = effectivenessBreakdown.factorsAssessed < effectivenessBreakdown.totalFactors
        ? ` (${effectivenessBreakdown.factorsAssessed}/${effectivenessBreakdown.totalFactors} factors assessed)`
        : '';
      setFieldValues((prev) => ({
        ...prev,
        control_effectiveness: `${effectivenessBreakdown.score}%${confidenceSuffix}`,
      }));
    }
  }, [effectivenessBreakdown]);

  // Auto-calculate gap_to_target from current_maturity and target_maturity
  useEffect(() => {
    const current = maturityToScore(fieldValues['current_maturity']);
    const target  = maturityToScore(fieldValues['target_maturity']);
    if (current === null || target === null) return;
    const gap = target - current;
    const label = gap > 0 ? `+${gap} level${gap !== 1 ? 's' : ''}` : gap === 0 ? 'On target' : `${Math.abs(gap)} level${Math.abs(gap) !== 1 ? 's' : ''} ahead`;
    setFieldValues((prev) => ({ ...prev, gap_to_target: label }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues['current_maturity'], fieldValues['target_maturity']]);

  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      if (id) return controlApi.update(id, data);
      return controlApi.create(data);
    },
    onSuccess: () => {
      // Invalidate all control-related caches so pages like gap analysis, reports,
      // and the controls list pick up the change when the user navigates there.
      // Partial-key invalidation only marks data stale — no extra API calls are
      // made until a subscribed component actually mounts/re-renders on that page.
      queryClient.invalidateQueries({ queryKey: ['control'] });      // ['control', id]
      queryClient.invalidateQueries({ queryKey: ['controls'] });     // ['controls', matrixId]
      queryClient.invalidateQueries({ queryKey: ['controls-soa'] }); // all SOA queries
      queryClient.invalidateQueries({ queryKey: ['reports'] });      // gap analysis / reports page
      setIsEditing(false);
      toast.success('Control updated.');
      // RUB-50672: a Risk's residual score only otherwise recomputes when someone opens that
      // Risk's own Detail page — this control's maturity/evidence/effectiveness just changed,
      // so refresh every Risk linked to it too, without waiting for someone to notice and open
      // each one. Best-effort and non-blocking: it must never delay or fail this save's own
      // success feedback. `id` is only set when this was an update to an existing control — a
      // brand-new control can't have any Risk linked to it yet.
      if (id) {
        recalculateRisksLinkedToControl(id).catch((error) => {
          console.error(`Failed to recalculate risks linked to control ${id}:`, error);
        });
      }
    },
    onError: (error: Error) => {
      console.error('Save error:', error);
      toast.error(`Failed to save control: ${error.message}`);
    },
  });

  const acceptanceMutation = useMutation({
    mutationFn: async (acceptanceFields: Record<string, string>) =>
      controlApi.update(id!, {
        matrixId: effectiveMatrixId,
        tenantId: getTenantId(),
        fields: { ...fieldValues, ...acceptanceFields },
      }),
    onSuccess: (_: any, acceptanceFields: Record<string, string>) => {
      setFieldValues((prev) => ({ ...prev, ...acceptanceFields }));
      queryClient.invalidateQueries({ queryKey: ['control', id] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      setEditingAcceptance(false);
      toast.success(acceptanceFields.risk_acceptance_reason ? 'Risk acceptance recorded.' : 'Risk acceptance cleared.');
    },
    onError: (error: Error) => toast.error(`Failed to save: ${error.message}`),
  });

  const handleFieldChange = (fieldId: string, value: any) => {
    setFieldValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
    if (invalidFieldIds.has(fieldId)) {
      setInvalidFieldIds((prev) => {
        const next = new Set(prev);
        next.delete(fieldId);
        return next;
      });
    }
  };

  const handleSave = () => {
    if (!effectiveMatrixId) {
      toast.warning('Missing matrixId. Please open this control from its matrix.');
      return;
    }

    const missing = getMissingRequiredFields(effectiveFieldConfigs, fieldValues);
    if (missing.length > 0) {
      setInvalidFieldIds(new Set(missing));
      const names = effectiveFieldConfigs.filter((c) => missing.includes(c.fieldId)).map((c) => c.fieldName).join(', ');
      toast.error(`Please fill in required fields: ${names}`);
      return;
    }
    setInvalidFieldIds(new Set());

    const dataToSave: Record<string, any> = { ...fieldValues };

    fieldConfigs.forEach((config) => {
      if (!config.isEditable) {
        delete dataToSave[config.fieldId];
      }
    });

    const payload = {
      matrixId: effectiveMatrixId,
      tenantId: getTenantId(),
      fields: dataToSave,
    };

    saveMutation.mutate(payload);
  };

  if (!matrixId && !id) {
    // Only require matrixId for new controls, not for viewing existing ones
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="np-banner np-tone-warning">
          <i className="ri-error-warning-line"></i>
          <span>Missing <strong>matrixId</strong> query param for new control. Example: <code>#/controls/new?matrixId=MATRIX-123</code></span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <div className="np-page-head" style={{ alignItems: 'flex-start' }}>
        <div className="d-flex align-items-start gap-3">
          <span className="np-icon-badge np-tone-primary flex-shrink-0">
            <i className="ri-shield-check-line"></i>
          </span>
          <div>
            <h1>{id ? 'Edit Control' : 'New Control'}</h1>
            {id && (
              <div className="d-flex align-items-center gap-2">
                <span className="np-chip np-tone-neutral np-tabular">
                  {String(fieldValues.control_id || id)}
                </span>
                {(fieldValues.control_title || fieldValues.title || fieldValues.name) && (
                  <span className="np-sub">
                    {String(fieldValues.control_title || fieldValues.title || fieldValues.name)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="np-page-actions">
          {!isEditing ? (
            <>
              <button className="np-btn np-btn-primary" onClick={() => { setIsEditing(true); setInvalidFieldIds(new Set()); }}>
                <i className="ri-edit-line"></i>
                Edit
              </button>
              {id && (
                <button
                  className="np-btn np-btn-ghost"
                  onClick={() => setShowRaiseModal(true)}
                  title="Raise Issue, Incident, or Remediation against this control"
                >
                  <i className="ri-add-circle-line"></i>
                  Raise
                </button>
              )}
              <button className="np-btn np-btn-ghost" onClick={() => window.history.back()}>
                <i className="ri-arrow-left-line"></i>
                Back
              </button>
            </>
          ) : (
            <>
              <button
                className="np-btn np-btn-success"
                onClick={handleSave}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? (
                  <>
                    <span className="spinner-border spinner-border-sm"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line"></i>
                    Save
                  </>
                )}
              </button>
              {id && (
                <button
                  className="np-btn np-btn-ghost"
                  onClick={() => {
                    setIsEditing(false);
                    setInvalidFieldIds(new Set());
                    if (control?.fields) {
                      const sanitizedFields: FieldValues = {};
                      Object.keys(control.fields).forEach((key) => {
                        const val = control.fields[key];
                        if (val && typeof val === 'object' && 'value' in val) {
                          sanitizedFields[key] = val.value;
                        } else {
                          sanitizedFields[key] = val;
                        }
                      });
                      setFieldValues(sanitizedFields);
                    }
                  }}
                >
                  <i className="ri-close-line"></i>
                  Cancel
                </button>
              )}
              <button className="np-btn np-btn-ghost" onClick={() => window.history.back()}>
                <i className="ri-arrow-left-line"></i>
                Back
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Tab navigation ── */}
      <div className="np-tabs">
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'details' ? ' active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          <i className="ri-shield-check-line"></i>Control Details
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'attachments' ? ' active' : ''}`}
          onClick={() => setActiveTab('attachments')}
        >
          <i className="ri-attachment-2"></i>Attachments
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'raci' ? ' active' : ''}`}
          onClick={() => setActiveTab('raci')}
        >
          <i className="ri-team-line"></i>RACI
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'requirements' ? ' active' : ''}`}
          onClick={() => {
            setActiveTab('requirements');
            // The Requirements tab has its own Add/Delete/Status/RACI controls but no save
            // button of its own - update_control's PUT handler REPLACES the whole `fields`
            // object rather than merging (confirmed live: item['fields'] = validated_fields,
            // no merge against the existing record), so a narrower, tab-local save call risks
            // silently wiping every other field on the control the moment its payload shape
            // drifts from handleSave's. Reusing the exact same Save button/saveMutation every
            // other field on this page already goes through is the only way to avoid that class
            // of bug - entering Edit mode here makes that dependency visible (the Save/Cancel
            // buttons appear in the header) instead of the previous silent no-op, where clicking
            // Add visibly updated the on-screen table via local fieldValues state but never
            // reached the backend unless the user separately remembered to click Edit first.
            setIsEditing(true);
          }}
        >
          <i className="ri-task-line"></i>Requirements
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'artefacts' ? ' active' : ''}`}
          onClick={() => setActiveTab('artefacts')}
        >
          <i className="ri-links-line"></i>Linked Artefacts
        </button>
      </div>

      {/* ── ISO Conformity & Risk Acceptance ── */}
      {(() => {
        const framework = String(fieldValues['framework'] || '');
        if (!/iso/i.test(framework)) return null;
        if (evidenceLoading) return null;
        const assignedScope = String(fieldValues['assigned_scope'] || '');
        if (assignedScope === 'Out of Scope' || assignedScope === 'Not Applicable') return null;

        const reasons: string[] = [];
        const implStatus = String(fieldValues['implementation_status'] || '').toLowerCase();
        // A control marked "Not Applicable" has nothing to implement, same reasoning as the
        // assigned_scope carve-out above - isImplementationComplete alone would read it as
        // incomplete (its status starts with "not"), which is a different thing from actually
        // being an outstanding gap.
        if (!implStatus.includes('not applicable') && !isImplementationComplete(implStatus)) {
          reasons.push('Control is not fully implemented');
        }
        const currentMaturityRaw = String(fieldValues['current_maturity'] || '').trim();
        if (currentMaturityRaw !== '') {
          if (currentMaturityRaw.toLowerCase() === 'unassessed') {
            reasons.push('Maturity not yet assessed');
          } else {
            const currentMatScore = maturityToScore(currentMaturityRaw);
            if (currentMatScore === null) reasons.push('Maturity not yet assessed');
            else if (currentMatScore < 2) reasons.push('Maturity is below Repeatable (level 2)');
          }
        }
        const totalLinked = Array.isArray((control as any)?.linked_artefacts) ? (control as any).linked_artefacts.length : 0;
        if (totalLinked === 0) reasons.push('No evidence linked to this control');
        if (linkedEvidenceObjs.length > 0) {
          const today = new Date(); today.setHours(0, 0, 0, 0);
          // next_due_date is what every real write path actually populates
          // (CollectionScheduleCard.tsx, lambda/prod-nimbus-evidence's review_submission()) -
          // evidence_next_review/next_review_date are never written anywhere, which made this
          // check structurally unable to ever fire against real data.
          const overdueItems = linkedEvidenceObjs.filter((ev: any) => {
            const reviewStr = String(ev.fields?.next_due_date || '');
            if (reviewStr) { const d = new Date(reviewStr); if (!isNaN(d.getTime()) && d < today) return true; }
            const st = String(ev.status || ev.fields?.status || ev.fields?.evidence_status || '').toLowerCase();
            return st === 'expired' || st === 'overdue' || st === 'rejected';
          });
          const itemsWithSchedule = linkedEvidenceObjs.filter((ev: any) => !!ev.fields?.next_due_date);
          if ((itemsWithSchedule.length > 0 || overdueItems.length > 0) && overdueItems.length > 0)
            reasons.push('Evidence overdue for review');
          // Multi-team rollup: an item with submissions is only "reviewed" once EVERY one of
          // its submissions is Accepted - a requirement isn't actually evidenced until every
          // team has come through, not just some of them. An item with no submissions falls
          // back to its own review-date fields exactly as before.
          const hasUnreviewed = linkedEvidenceObjs.some((ev: any) => {
            const subs = submissionsByEvidenceId.get(ev.evidenceId) || [];
            if (subs.length > 0) return subs.some((s: any) => s.status !== 'Accepted');
            return !String(ev.fields?.evidence_last_reviewed || ev.fields?.last_reviewed || ev.fields?.last_review_date || '');
          });
          if (hasUnreviewed) reasons.push('Evidence not yet reviewed');
        }
        const hasRaci = (raciAssignments as any[]).some((r: any) => r.role === 'R' || r.role === 'A');
        if (!hasRaci) reasons.push('No RACI ownership assigned (Responsible or Accountable)');

        // Acceptance state
        const acceptanceReason = String(fieldValues['risk_acceptance_reason'] || '').trim();
        const acceptanceOwner  = String(fieldValues['risk_acceptance_owner']  || '').trim();
        const acceptanceUntil  = String(fieldValues['risk_acceptance_until']  || '').trim();
        const acceptanceDate   = String(fieldValues['risk_acceptance_date']   || '').trim();
        const acceptanceReviewPeriod = String(fieldValues['risk_acceptance_review_period'] || '').trim();
        const untilDate = acceptanceUntil ? new Date(acceptanceUntil) : null;
        const todayCheck = new Date(); todayCheck.setHours(0, 0, 0, 0);
        const acceptanceExpired = untilDate ? (!isNaN(untilDate.getTime()) && untilDate < todayCheck) : false;
        const isAccepted = !!(acceptanceReason && acceptanceUntil && !acceptanceExpired);

        // ── Banner ──
        let banner: React.ReactNode;
        if (reasons.length === 0) {
          banner = (
            <div className="np-banner np-tone-success mb-4" role="alert">
              <i className="ri-shield-check-fill"></i>
              <div>
                <strong>ISO Conformity: No issues detected</strong>
                <p className="mb-0 mt-1">This control meets all operating criteria for {framework}.</p>
              </div>
            </div>
          );
        } else if (isAccepted) {
          banner = (
            <div className="np-banner np-tone-warning mb-4" role="alert">
              <i className="ri-shield-check-line"></i>
              <div className="flex-grow-1">
                <strong>Conformity Gap — Risk Accepted</strong>
                <p className="mb-0 mt-1">
                  This control has conformity gaps formally accepted until <strong>{acceptanceUntil}</strong>
                  {acceptanceOwner && <> by <strong>{acceptanceOwner}</strong></>}.
                </p>
                <details className="mt-2">
                  <summary style={{ cursor: 'pointer', opacity: 0.75 }}>
                    View outstanding gaps ({reasons.length})
                  </summary>
                  <ul className="mb-0 mt-1">{reasons.map((r) => <li key={r}>{r}</li>)}</ul>
                </details>
              </div>
            </div>
          );
        } else {
          banner = (
            <div className="np-banner np-tone-danger mb-4" role="alert">
              <i className="ri-shield-cross-fill"></i>
              <div>
                <strong>ISO Conformity Risk — {framework}</strong>
                {acceptanceExpired && (
                  <p className="mb-0 mt-1 fw-medium">
                    <i className="ri-alarm-warning-line me-1"></i>
                    Risk acceptance expired on {acceptanceUntil} — please review.
                  </p>
                )}
                <p className="mb-0 mt-1">This control may generate a nonconformity finding at audit. Resolve the following:</p>
                <ul className="mb-0 mt-1">{reasons.map((r) => <li key={r}>{r}</li>)}</ul>
              </div>
            </div>
          );
        }

        // ── Acceptance card ──
        const acceptanceSection = reasons.length > 0 ? (
          <div className="np-card mb-4">
            <div className="np-card-head">
              <h2><i className="ri-shield-user-line text-warning"></i> Risk Acceptance</h2>
              {!editingAcceptance && (
                <>
                  <button
                    className="np-btn np-btn-ghost np-btn-sm"
                    onClick={() => {
                      setAcceptanceDraft({ reason: acceptanceReason, owner: acceptanceOwner, period: acceptanceReviewPeriod, until: acceptanceUntil, on: acceptanceDate || new Date().toISOString().split('T')[0] });
                      setEditingAcceptance(true);
                    }}
                  >
                    <i className={isAccepted || acceptanceExpired ? 'ri-edit-line' : 'ri-add-line'}></i>
                    {isAccepted || acceptanceExpired ? 'Edit' : 'Record Acceptance'}
                  </button>
                  {(isAccepted || acceptanceExpired) && (
                    <button
                      className="np-btn np-btn-ghost np-btn-sm"
                      onClick={async () => {
                        if (!(await swal.confirm('Remove risk acceptance?', 'The control will show as an unaddressed conformity risk.'))) return;
                        acceptanceMutation.mutate({ risk_acceptance_reason: '', risk_acceptance_owner: '', risk_acceptance_until: '', risk_acceptance_date: '', risk_acceptance_review_period: '' });
                      }}
                    >
                      <i className="ri-delete-bin-line"></i>Clear
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="np-card-body">
              {editingAcceptance ? (
                <div>
                  <div className="np-field">
                    <label>Acceptance Reason <span className="text-danger">*</span></label>
                    <style>{`.acceptance-editor .ql-editor { min-height: 100px; }`}</style>
                    <ReactQuill
                      className="acceptance-editor"
                      theme="snow"
                      value={acceptanceDraft.reason}
                      onChange={(val) => setAcceptanceDraft((d) => ({ ...d, reason: val }))}
                      modules={{ toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']] }}
                    />
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6 np-field">
                      <label>Accepted On</label>
                      <input
                        type="date"
                        className="np-input"
                        value={acceptanceDraft.on}
                        onChange={(e) => {
                          const on = e.target.value;
                          const until = calcAcceptanceUntil(acceptanceDraft.period, on);
                          setAcceptanceDraft((d) => ({ ...d, on, ...(d.period !== 'Ad-hoc' && until ? { until } : {}) }));
                        }}
                      />
                    </div>
                    <div className="col-md-6 np-field">
                      <label>Review Period <span className="text-danger">*</span></label>
                      <select
                        className="np-select"
                        value={acceptanceDraft.period}
                        onChange={(e) => {
                          const period = e.target.value;
                          const until = calcAcceptanceUntil(period, acceptanceDraft.on);
                          setAcceptanceDraft((d) => ({ ...d, period, ...(period !== 'Ad-hoc' && until ? { until } : period === 'Ad-hoc' ? { until: '' } : {}) }));
                        }}
                      >
                        <option value="">— Select period —</option>
                        {ACCEPTANCE_REVIEW_PERIODS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6 np-field">
                      <label>Accepted By</label>
                      <Select
                        styles={reactSelectStyles()}
                        isClearable
                        placeholder="Search people…"
                        options={(people as Person[]).map((p: any) => {
                          const name = p.personName || [p.firstName, p.lastName].filter(Boolean).join(' ') || String(p.personId || '');
                          return { value: name, label: name };
                        })}
                        value={acceptanceDraft.owner ? { value: acceptanceDraft.owner, label: acceptanceDraft.owner } : null}
                        onChange={(opt) => setAcceptanceDraft((d) => ({ ...d, owner: opt?.value ?? '' }))}
                      />
                    </div>
                    <div className="col-md-6 np-field">
                      <label>Accepted Until <span className="text-danger">*</span></label>
                      {acceptanceDraft.period === 'Ad-hoc' ? (
                        <input
                          type="date"
                          className="np-input"
                          value={acceptanceDraft.until}
                          onChange={(e) => setAcceptanceDraft((d) => ({ ...d, until: e.target.value }))}
                        />
                      ) : (
                        <input
                          type="text"
                          className="np-input"
                          readOnly
                          style={{ background: 'var(--np-surface-2)' }}
                          value={acceptanceDraft.until || (acceptanceDraft.period ? 'Set Accepted On + Review Period' : '—')}
                        />
                      )}
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="np-btn np-btn-success np-btn-sm"
                      disabled={!acceptanceDraft.reason.replace(/<[^>]*>/g, '').trim() || !acceptanceDraft.until || !acceptanceDraft.period || acceptanceMutation.isPending}
                      onClick={() => acceptanceMutation.mutate({
                        risk_acceptance_reason: acceptanceDraft.reason.trim(),
                        risk_acceptance_owner: acceptanceDraft.owner.trim(),
                        risk_acceptance_review_period: acceptanceDraft.period,
                        risk_acceptance_until: acceptanceDraft.until,
                        risk_acceptance_date: acceptanceDraft.on || new Date().toISOString().split('T')[0],
                      })}
                    >
                      {acceptanceMutation.isPending
                        ? <><span className="spinner-border spinner-border-sm"></span>Saving...</>
                        : <><i className="ri-save-line"></i>Save Acceptance</>}
                    </button>
                    <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => setEditingAcceptance(false)} disabled={acceptanceMutation.isPending}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : isAccepted || acceptanceExpired ? (
                <div className="row g-2">
                  <div className="col-12">
                    <span className="np-hint">Reason:</span>
                    <div className="mb-1 mt-1" dangerouslySetInnerHTML={{ __html: acceptanceReason || '—' }} />
                  </div>
                  <div className="col-md-3">
                    <span className="np-hint">Accepted On:</span>
                    <p className="mb-0 mt-1">{acceptanceDate || '—'}</p>
                  </div>
                  <div className="col-md-3">
                    <span className="np-hint">Review Period:</span>
                    <p className="mb-0 mt-1 fw-medium">{acceptanceReviewPeriod || '—'}</p>
                  </div>
                  <div className="col-md-3">
                    <span className="np-hint">Accepted By:</span>
                    <p className="mb-0 mt-1 fw-medium">{acceptanceOwner || '—'}</p>
                  </div>
                  <div className="col-md-3">
                    <span className="np-hint">Accepted Until:</span>
                    <p className={`mb-0 mt-1 fw-medium${acceptanceExpired ? ' text-danger' : ''}`}>
                      {acceptanceUntil}{acceptanceExpired && <span className="ms-1"><i className="ri-alarm-warning-line"></i> Expired</span>}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="np-hint mb-0">
                  <i className="ri-shield-user-line me-1"></i>No risk acceptance recorded. Click <strong>Record Acceptance</strong> to formally document that this
                  conformity gap has been reviewed and accepted with a named owner and expiry date.
                </p>
              )}
            </div>
          </div>
        ) : null;

        return <>{banner}{acceptanceSection}</>;
      })()}

      {activeTab === 'details' && (
        <>
      <div className="row">
        {Object.keys(groupedFields)
          .sort((a, b) => Number(a) - Number(b))
          .map((cardNum) => {
            const cardFields = groupedFields[Number(cardNum)]
              .slice()
              .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

            if (cardFields.length === 0) return null;

            const cardName = cardDefinitions[String(cardNum)] || `Card ${cardNum}`;

            return (
              <React.Fragment key={cardNum}>
                <div className="col-12 mb-4">
                  <div className="np-card">
                    <div className="np-card-head">
                      <h2>{cardName}</h2>
                    </div>
                    <div className="np-card-body">
                      {cardFields.map((config) => (
                          <FieldRenderer
                            key={config.fieldId}
                            config={config}
                            value={fieldValues[config.fieldId]}
                            onChange={handleFieldChange}
                            error={invalidFieldIds.has(config.fieldId) ? 'This field is required.' : undefined}
                            people={people}
                            departments={departments}
                          />
                        ))}
                    </div>
                  </div>
                </div>
                {Number(cardNum) === 1 && (
                  <div className="col-12 mb-4">
                    <div className="np-card">
                      <div className="np-card-head">
                        <i className="ri-focus-3-line text-primary"></i>
                        <h2>Applicability</h2>
                        {fieldValues['assigned_scope'] && (
                          <span className={`np-chip np-tone-${
                            fieldValues['assigned_scope'] === 'In Scope'
                              ? 'success'
                              : fieldValues['assigned_scope'] === 'Out of Scope'
                              ? 'danger'
                              : 'neutral'
                          }`}>
                            {fieldValues['assigned_scope'] as string}
                          </span>
                        )}
                      </div>
                      <div className="np-card-body">
                        {isEditing && (
                          <div className="np-field">
                            <label>Assigned Scope</label>
                            <select
                              className="np-select"
                              value={(fieldValues['assigned_scope'] as string) || ''}
                              onChange={(e) => handleFieldChange('assigned_scope', e.target.value)}
                            >
                              <option value="">— Select scope —</option>
                              <option value="In Scope">In Scope</option>
                              <option value="Out of Scope">Out of Scope</option>
                              <option value="Not Applicable">Not Applicable</option>
                            </select>
                          </div>
                        )}

                        {(fieldValues['assigned_scope'] === 'Out of Scope' ||
                          (!isEditing && fieldValues['justification_for_exclusion'])) && (
                          <div className="np-field">
                            <label>Justification for Exclusion</label>
                            {isEditing ? (
                              <>
                                <style>{`.exclusion-editor .ql-editor { min-height: 96px; resize: vertical; overflow-y: auto; }`}</style>
                                <ReactQuill
                                  className="exclusion-editor"
                                  theme="snow"
                                  value={(fieldValues['justification_for_exclusion'] as string) || ''}
                                  onChange={(val) => handleFieldChange('justification_for_exclusion', val)}
                                  modules={{ toolbar: [['bold','italic','underline'],[{list:'ordered'},{list:'bullet'}],['link'],['clean']] }}
                                />
                              </>
                            ) : (
                              <div
                                className="np-input"
                                style={{ minHeight: '2rem' }}
                                dangerouslySetInnerHTML={{ __html: (fieldValues['justification_for_exclusion'] as string) || '<span class="np-hint">—</span>' }}
                              />
                            )}
                          </div>
                        )}

                        {(fieldValues['assigned_scope'] === 'In Scope' ||
                          (!isEditing && justificationForInclusionValues.length > 0)) && (
                          <div className="np-field">
                            <label>Justification for Inclusion</label>
                            {isEditing ? (
                              <Select
                                isMulti
                                isClearable
                                isSearchable
                                menuPortalTarget={document.body}
                                styles={reactSelectStyles(false)}
                                options={JUSTIFICATION_OPTIONS}
                                value={JUSTIFICATION_OPTIONS.filter((opt) =>
                                  justificationForInclusionValues.includes(opt.value)
                                )}
                                onChange={(opts) =>
                                  handleFieldChange('justification_for_inclusion', (opts as any[]).map((o) => o.value))
                                }
                                placeholder="Select justification(s)"
                              />
                            ) : justificationForInclusionValues.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {justificationForInclusionValues.map((v) => (
                                  <span key={v} className="np-chip np-tone-neutral">{v}</span>
                                ))}
                              </div>
                            ) : (
                              <p className="np-hint mb-0">—</p>
                            )}
                          </div>
                        )}

                        {!fieldValues['assigned_scope'] && !isEditing && (
                          <p className="np-hint mb-0">
                            <i className="ri-information-line me-1"></i>
                            No scope assigned yet. Click Edit to set applicability.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
      </div>

      {/* Control Effectiveness Card */}
      <div className="row">
        <div className="col-12 mb-4">
          <div className="np-card">
            <div className="np-card-head">
              <i className="ri-bar-chart-line text-primary"></i>
              <h2 className="d-flex align-items-center gap-1">
                Control Effectiveness
                <span
                  className="d-inline-flex position-relative"
                  tabIndex={0}
                  style={{ cursor: 'help' }}
                  title={
                    'Weighted average of up to three factors, each excluded (and its weight ' +
                    'redistributed to the others) until it has real data behind it:\n\n' +
                    'Maturity (50%) - Current Maturity rating, 0-5 scale. Excluded while left at ' +
                    '"Unassessed".\n\n' +
                    'Evidence presence (30%) - graduated by count of linked evidence: 0 linked = 0%, ' +
                    '1 = 60%, 2 = 85%, 3+ = 100%. Always counted (zero evidence is a known state, not ' +
                    'a missing assessment).\n\n' +
                    'Test recency (20%) - how overdue the last test is against Test Frequency. ' +
                    'Excluded if no Last Test Date has ever been logged.'
                  }
                >
                  <i className="ri-information-line np-hint" style={{ fontSize: '0.9rem' }}></i>
                </span>
              </h2>
              {computedEffectiveness !== null && (
                <span className="np-chip np-tone-neutral">Auto-calculated</span>
              )}
            </div>
            <div className="np-card-body">
              {computedEffectiveness === null || !effectivenessBreakdown ? (
                <p className="np-hint mb-0">
                  <i className="ri-information-line me-1"></i>
                  Set <strong>Current Maturity</strong>, link some <strong>Evidence</strong>, or log a{' '}
                  <strong>Last Test Date</strong> to calculate effectiveness.
                </p>
              ) : (
                <div className="row align-items-center">
                  <div className="col-md-3 text-center">
                    <div
                      className={`np-score-display np-tabular np-tone-${
                        computedEffectiveness >= 71 ? 'success'
                        : computedEffectiveness >= 41 ? 'warning'
                        : 'danger'
                      }`}
                    >
                      {computedEffectiveness}%
                    </div>
                    <span
                      className={`np-chip np-tone-${
                        computedEffectiveness >= 71 ? 'success'
                        : computedEffectiveness >= 41 ? 'warning'
                        : 'danger'
                      } mt-1`}
                    >
                      {computedEffectiveness >= 71 ? 'Effective' : computedEffectiveness >= 41 ? 'Partially Effective' : 'Ineffective'}
                    </span>
                    {effectivenessBreakdown.factorsAssessed < effectivenessBreakdown.totalFactors && (
                      <div className="np-hint mt-2">
                        <i className="ri-error-warning-line me-1"></i>
                        Based on {effectivenessBreakdown.factorsAssessed}/{effectivenessBreakdown.totalFactors} factors
                      </div>
                    )}
                    {effectivenessBreakdown.cappedByImplementation && (
                      <div className="np-hint mt-2">
                        <i className="ri-error-warning-line me-1"></i>
                        Capped at 40% — not fully implemented
                      </div>
                    )}
                  </div>
                  <div className="col-md-9">
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <small className="np-hint">Overall Effectiveness</small>
                        <small className="fw-semibold np-tabular">{computedEffectiveness}%</small>
                      </div>
                      <div className="np-progress" style={{ height: '12px' }}>
                        <div
                          className={`np-fill np-tone-${
                            computedEffectiveness >= 71 ? 'success'
                            : computedEffectiveness >= 41 ? 'warning'
                            : 'danger'
                          }`}
                          style={{ width: `${computedEffectiveness}%` }}
                          role="progressbar"
                        />
                      </div>
                    </div>
                    <div className="row g-2 mt-1">
                      {effectivenessBreakdown.factors.map((f) => (
                        <div className="col-4" key={f.key}>
                          <small className="np-hint d-block">
                            {f.label} ({Math.round(f.weight * 100)}%)
                            {f.key === 'testRecency' && effectivenessBreakdown.testRecencySource === 'evidence' && (
                              <span title="Derived from the most recently reviewed linked evidence, not the Testing card's own Last Test Date"> · from evidence</span>
                            )}
                          </small>
                          <small className="fw-semibold np-tabular">{Math.round(f.score)}%</small>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      </>
      )}

      {activeTab === 'requirements' && (
        <div className="row">
          {id ? (
            <RequirementsTracker
              entityType="control"
              entityId={id}
              functionalRequirements={(fieldValues['functional_requirements'] as any[]) || []}
              nonFunctionalRequirements={(fieldValues['non_functional_requirements'] as any[]) || []}
              onUpdate={(functional, nonFunctional) => {
                handleFieldChange('functional_requirements', functional);
                handleFieldChange('non_functional_requirements', nonFunctional);
              }}
              people={people}
            />
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-task-line"></i> Requirements</h2>
                </div>
                <div className="np-card-body">
                  <p className="mb-0 np-hint">Available after you save this control.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'attachments' && (
        <div className="row">
          {id ? (
            <DocumentManager entityType="control" entityId={id} readOnly={!isEditing} />
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-attachment-2"></i> Attachments</h2>
                </div>
                <div className="np-card-body">
                  <p className="mb-0 np-hint">Available after you save this control.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'raci' && (
        <div className="row">
          {id ? (
            <RaciAssignments artefactId={id} artefactType="control" readOnly={!isEditing} />
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-team-line"></i> RACI Assignments</h2>
                </div>
                <div className="np-card-body">
                  <p className="mb-0 np-hint">Available after you save this control.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'artefacts' && (
        <div className="row">
          {id ? (
            <LinkedArtefacts
              artefactId={id}
              artefactType="control"
              artefactRegister="control_register"
              matrixId={effectiveMatrixId}
              readOnly={!isEditing}
            />
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-links-line"></i> Linked Artefacts</h2>
                </div>
                <div className="np-card-body">
                  <p className="mb-0 np-hint">Available after you save this control.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {showRaiseModal && id && (
        <RaiseArtefactModal
          controlId={id}
          controlTitle={String(fieldValues.control_title || fieldValues.title || fieldValues.name || '')}
          controlRegister={effectiveMatrixId}
          onClose={() => setShowRaiseModal(false)}
        />
      )}
    </div>
  );
};
